import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import styles from "./ModalShell.module.css";

interface ModalShellProps {
  isOpen: boolean;
  /** Id of the element naming the dialog — usually the heading inside `children`. */
  labelledBy: string;
  /**
   * Whether Escape and a click on the backdrop close it. False while a confirmation
   * is in flight, so the member cannot dismiss a dialog mid-mutation and be left
   * unsure whether it happened.
   */
  dismissible?: boolean;
  onDismiss: () => void;
  /** Extra class on the panel, for dialogs that need more room than the default. */
  panelClassName?: string;
  children: ReactNode;
}

/**
 * The plumbing every modal in the app needs and none of them should own: the
 * backdrop, Escape-to-close, click-outside-to-close, the ARIA wiring and moving
 * focus into the panel on open.
 *
 * Extracted when the checkout currency-switch dialog needed the same behaviour as
 * ConfirmModal but entirely different content — a second hand-rolled copy of this
 * is how modal behaviour drifts apart.
 */
export function ModalShell({
  isOpen,
  labelledBy,
  dismissible = true,
  onDismiss,
  panelClassName,
  children,
}: ModalShellProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape" && dismissible) onDismiss();
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, dismissible, onDismiss]);

  // Lock page scrolling while the dialog is up, or the page keeps its own scrollbar beside the
  // overlay and can still be scrolled underneath it.
  //
  // Both elements are set, not just body: body's overflow only reaches the viewport through the
  // propagation rule (root visible -> use body's), which anything else setting an overflow on
  // html quietly defeats. The root element applies to the viewport directly.
  //
  // Runs before the focus effect below so the page is already frozen by the time focus moves.
  // The previous values are saved and restored rather than cleared, so a dialog opened over
  // another leaves the lock in place when it closes and only the outermost one releases it.
  useEffect(() => {
    if (!isOpen) return;
    const root = document.documentElement;
    const previousBodyOverflow = document.body.style.overflow;
    const previousRootOverflow = root.style.overflow;
    document.body.style.overflow = "hidden";
    root.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousBodyOverflow;
      root.style.overflow = previousRootOverflow;
    };
  }, [isOpen]);

  // Move focus into the dialog on open: it covers the page, so a keyboard user
  // left behind on the underlying form has no way to reach its controls.
  //
  // preventScroll matters. focus() otherwise scrolls every ancestor scroll container to reveal
  // the element, and the panel sits inside a position:fixed overlay that is already fully on
  // screen - so any scrolling it triggers is pure side effect, yanking the page under the dialog.
  useEffect(() => {
    if (isOpen) panelRef.current?.focus({ preventScroll: true });
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === overlayRef.current && dismissible) onDismiss();
  };

  /*
   * Rendered into <body> rather than where it is mounted. A dialog opened from deep inside a
   * page otherwise stays a DOM descendant of whatever opened it, and inherits that subtree's
   * behaviour: the browser's native tap highlight walks up into the ancestor and flashes it
   * behind the dialog, and out-of-flow descendants resolve their containing block against the
   * wrong element. Both were real bugs in the checkout's pick-up point picker.
   */
  return createPortal(
    <div
      ref={overlayRef}
      className={styles.overlay}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledBy}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        className={
          panelClassName ? `${styles.modal} ${panelClassName}` : styles.modal
        }
      >
        {children}
      </div>
    </div>,
    document.body
  );
}
